import { useTranslation } from 'language/translator'
import { shallowEqual, useSelector } from 'react-redux'

import {
  selectCustomKpis,
  selectHeadlineMetric,
  selectMetricsFetched,
  NO_MISSING_SLICES,
  selectMetricRawData,
  selectMetrics,
  selectMetricFilterOptions,
  selectOneMetric
} from 'redux/selectors/Metrics'
import { ApplicationState } from 'redux/Stores/types'
import {
  IMetricRawData,
  TDefinitionSlice,
  TMetricSettings,
  TMetricQueryParams,
  TMetricSourceRef
} from 'types/GlobalMetric'
import { getMetricDefaultSettings } from 'utils/metric'

/**
 * Hook for getting one metric.
 * If variantId is provided, the returned metric's distributions,
 * rolling_sums and slices are replaced with those of the variant metric.
 * if not then default is used
 * @returns metric | null
 */
export const useMetric = (
  id: string | null | undefined,
  variantId: string | null | undefined
) => {
  const { lang } = useTranslation()

  return useSelector((state: ApplicationState) => {
    if (!id) return null

    return selectOneMetric(state, id, lang, variantId)
  })
}

/**
 * Hook for all metrics
 * @returns [metrics, loading]
 */
export const useMetrics = () => {
  const { lang } = useTranslation()

  return useSelector((state: ApplicationState) => selectMetrics(state, lang))
}

/**
 * Hook for getting the filter options for one metric.
 * Used for metric page.
 * @returns { data, loading }
 */
export const useMetricFilters = (id: string) => {
  const { lang } = useTranslation()

  return useSelector((state: ApplicationState) =>
    selectMetricFilterOptions(state, id, lang)
  )
}

const EMPTY_SETTINGS: TMetricSettings = {
  variant_kpi_template_id: null,
  rolling_sum: null,
  distribution: null,
  slices: []
}

// TODO: remove optional settings after merge with HP-14274
export const useMetricRawData = (params: {
  id?: string | null
  settings?: TMetricSettings
  queryParams?: TMetricQueryParams
  source?: TMetricSourceRef
  includeKeyFigure?: boolean
}): [IMetricRawData | null, boolean, TDefinitionSlice[]] => {
  const { id, settings, queryParams, source, includeKeyFigure } = params

  // shallowEqual: the selector builds a fresh [data, error] tuple on every
  // store update; without it every consumer re-renders on any Redux action.
  return useSelector(
    (
      state: ApplicationState
    ): [IMetricRawData | null, boolean, TDefinitionSlice[]] => {
      if (!id) return [null, false, NO_MISSING_SLICES]

      const metric = state.MetricStore.data[id]

      const defaultSettings = getMetricDefaultSettings(
        metric,
        settings?.variant_kpi_template_id
      )

      const periodFilter = defaultSettings?.period_filter

      const body = {
        ...(settings || EMPTY_SETTINGS),
        period_filter: periodFilter,
        query_params: queryParams,
        source,
        include_key_figure: includeKeyFigure
      }

      return selectMetricRawData(state, id, body)
    },
    shallowEqual
  )
}

export const useCustomKpis = () => {
  return useSelector(selectCustomKpis)
}

/**
 * Hook for the metric the results page leads with, with the settings to show
 * it at. `resolved` is false until the backend has answered.
 * @returns { metric, resolved }
 */
export const useHeadlineMetric = () => {
  return useSelector(selectHeadlineMetric, shallowEqual)
}

/**
 * Hook for whether the metric catalog has been fetched.
 * @returns boolean
 */
export const useMetricsFetched = () => {
  return useSelector(selectMetricsFetched)
}
